import { get, includes, some } from 'lodash';
import { addProtocol } from 'modules/taboola-common-frontend-modules/utils/urlUtils';
import { validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { validationsApi } from 'services/api';

export const accessibleUrlValidation = {
    validationFn: async (value, _, { formAccountId, bypassUrlResponseValidation, accessibleUrlCache }) => {
        if (bypassUrlResponseValidation || value === accessibleUrlCache.current) {
            return true;
        }
        return validateWebsiteUrlTracking(formAccountId, value);
    },
    messageId: 'validations.error.field.url.pixel',
    defaultMessage: `We couldn't find a Pixel base code on this website that matches your account. Please follow the previous steps to install the code`,
};

export const exactAccountIdMatchValidation = {
    validationFn: async (
        value,
        _,
        { formAccountId, bypassUrlResponseValidation, accessibleUrlCache, crawlerTimeoutMs }
    ) => {
        if (bypassUrlResponseValidation || value === accessibleUrlCache.current) {
            return true;
        }
        return validateWebsiteUrlTrackingExactAccountIdMatch(formAccountId, value, crawlerTimeoutMs);
    },
    messageId: 'validations.error.field.url.pixel',
    defaultMessage: `We couldn't find a Pixel base code on this website that matches your account. Please follow the previous steps to install the code`,
};

export const exactAccountIdMatchValidationWithContinue = {
    validationFn: async (
        value,
        _,
        { formAccountId, bypassUrlResponseValidation, accessibleUrlCache, crawlerTimeoutMs }
    ) => {
        if (bypassUrlResponseValidation || value === accessibleUrlCache.current) {
            return true;
        }
        return validateWebsiteUrlTrackingExactAccountIdMatch(formAccountId, value, crawlerTimeoutMs);
    },
    messageId: 'validations.error.field.url.pixel.with.continue.anyway',
    defaultMessage: `We couldn't find a Pixel base code on this website that matches your account. Please follow the previous steps to install the code. {continueAnyway}`,
};

export const emptyValidation = {
    validationFn: validationFunctions.isNotEmpty,
    messageId: 'validations.error.field.url.empty',
    defaultMessage: 'URL cannot be empty',
};

export const urlFormatValidation = {
    validationFn: value => {
        if (!value) return true;

        const urlWithProtocol = addProtocol(value);

        return validationFunctions.isURL(urlWithProtocol, { require_protocol: true });
    },
    messageId: 'validations.error.field.url.format.invalid',
    defaultMessage: 'Please enter a valid URL. Make sure it does not contain spaces or invalid characters.',
};

const protocolRegex = /^(https?:\/\/)/;
export const protocolValidation = {
    validationFn: (value, _) => protocolRegex.test(value),
    messageId: 'validations.error.field.url.protocol',
    defaultMessage: 'URL must include protocol (http:// or https://)',
};

export async function validateWebsiteUrlTracking(accountId, websiteUrl) {
    if (!websiteUrl) {
        return false;
    }

    try {
        const response = await validationsApi.validateWebsiteUrlTracking(accountId, websiteUrl);
        return (
            get(response, 'results.length', 0) > 0 &&
            some(response.results, result => includes(result.trackingMethods, 'TFA'))
        );
    } catch (e) {
        return false;
    }
}

export async function validateWebsiteUrlTrackingExactAccountIdMatch(accountId, websiteUrl, crawlerTimeoutMs) {
    if (!websiteUrl) {
        return false;
    }
    try {
        let response;

        try {
            response = await Promise.race([
                validationsApi.validateWebsiteUrlTracking(accountId, websiteUrl),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Crawler API request timeout')), crawlerTimeoutMs)
                ),
            ]);
        } catch (error) {
            return false;
        }

        const hasResults = get(response, 'results.length', 0) > 0;
        const hasExactMatch = some(response.results, result =>
            includes(result.trackingMethods, 'TFA_ACCOUNT_ID_EXACT_MATCH')
        );

        return hasResults && hasExactMatch;
    } catch (e) {
        return false;
    }
}
